import React, { useMemo } from 'react'
import { Check, ChevronRight } from 'lucide-react'

export type ToDoListItemProps = {
    icon?: React.ReactNode
    number: number
    title: string
    description?: string
    completed?: boolean
    onClick?: () => void
    className?: string
}

const TodoItem: React.FC<ToDoListItemProps> = ({
    number,
    title,
    description,
    completed = false,
    onClick,
    icon,
    className = '',
}) => {
    // Title color; keep description always neutral per design
    const textTitle = useMemo(() => (completed ? 'text-neutral-500' : 'text-black'), [completed])
    const descClass = 'text-neutral-500'

    const ariaLabel = `${number}. ${title}`

    const inner = (
        <>
            <div className="w-full h-6 inline-flex justify-start items-center gap-4 bg-transparent border-0 p-0">
                {completed ? (
                    <span className="w-6 h-6 bg-brand-grass rounded-full flex items-center justify-center" aria-hidden>
                        <Check size={14} strokeWidth={3} className="text-white" />
                    </span>
                ) : (
                    <span className="m-1">{icon}</span>
                )}

                {/* item number / title */}
                <div className="flex-1 justify-start text-sm font-medium font-['Inter'] leading-5">
                    <div className={`${textTitle} flex items-center gap-2`}>
                        <div className={`${textTitle} text-sm font-semibold leading-5`}>{number}.</div>
                        <div className={`${textTitle} text-sm font-semibold truncate`}>{title}</div>
                    </div>
                </div>

                {/* action area (right) - small chevron / indicator */}
                <div className="w-6 h-6 relative overflow-hidden flex items-center justify-center">
                    {!completed && <ChevronRight size={20} className="text-neutral-300" aria-hidden />}
                </div>
            </div>

            <div className="self-stretch pl-10 inline-flex justify-start items-start gap-2.5">
                <div className={`flex-1 text-sm font-normal font-['Inter'] leading-5 ${descClass} text-left`}>
                    {description}
                </div>
            </div>
        </>
    )

    // Interactive when not completed
    if (onClick) {
        return (
            <button
                type="button"
                role="listitem"
                onClick={onClick}
                className={`w-full p-2 gap-2.5 ${className} inline-flex flex-col justify-start items-start bg-transparent hover:bg-brand-dust transition-colors rounded-md`}
                aria-pressed={completed}
                aria-label={ariaLabel}
            >
                {inner}
                {!completed ? <span className="sr-only">Mark as completed</span> : null}
            </button>
        )
    }

    return (
        <div
            className={`w-full p-2 gap-2.5 ${className} inline-flex flex-col justify-start items-start bg-transparent rounded-md cursor-default`}
            role="listitem"
            aria-pressed={completed}
            aria-label={ariaLabel}
            aria-disabled="true"
        >
            {inner}
        </div>
    )
}

export default React.memo(TodoItem)
